/**
 * AbandonedOrderDetailPage.js - 弃单详情页
 * 展示弃单基础信息、收货信息、商品明细和金额汇总
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { adminAbandonedOrderApi } from '../../api/index';

const { Title, Text } = Typography;

const statusConfig = {
  abandoned: { color: 'orange', label: '未恢复' },
  recovered: { color: 'green', label: '已恢复' },
};

const formatMoney = (value) => `$${Number(value || 0).toFixed(2)}`;

const formatTime = (value) => (
  value ? new Date(value).toLocaleString('zh-CN', { hour12: false }) : '-'
);

export default function AbandonedOrderDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recovering, setRecovering] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchOrder = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminAbandonedOrderApi.getById(id);
      setOrder(data);
    } catch (err) {
      message.error('加载弃单详情失败：' + (err.message || '未知错误'));
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const handleRecover = async () => {
    setRecovering(true);
    try {
      await adminAbandonedOrderApi.recover(id);
      message.success('弃单已恢复');
      fetchOrder();
    } catch (err) {
      message.error('恢复失败：' + (err.message || '未知错误'));
    } finally {
      setRecovering(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await adminAbandonedOrderApi.delete(id);
      message.success('弃单已删除');
      navigate('/admin/abandoned-orders');
    } catch (err) {
      message.error('删除失败：' + (err.message || '未知错误'));
    } finally {
      setDeleting(false);
    }
  };

  const itemColumns = [
    {
      title: '商品',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text || '-'}</div>
          {record.variantTitle && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.variantTitle}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      width: 140,
      render: (sku) => (
        <span style={{ fontFamily: 'monospace', color: '#666' }}>
          {sku || '-'}
        </span>
      ),
    },
    {
      title: '单价',
      dataIndex: 'price',
      key: 'price',
      width: 100,
      align: 'right',
      render: formatMoney,
    },
    {
      title: '数量',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'center',
    },
    {
      title: '小计',
      dataIndex: 'total',
      key: 'total',
      width: 120,
      align: 'right',
      render: formatMoney,
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!order) {
    return (
      <div style={{ padding: 40 }}>
        <Text type="danger">弃单不存在或加载失败</Text>
        <Button style={{ marginLeft: 16 }} onClick={() => navigate('/admin/abandoned-orders')}>
          返回列表
        </Button>
      </div>
    );
  }

  const status = statusConfig[order.status] || { color: 'default', label: order.status || '-' };
  const items = order.items || [];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/abandoned-orders')}>
          返回列表
        </Button>
        <Title level={4} style={{ margin: 0 }}>
          弃单详情 #{order.orderNo}
        </Title>
        <Tag color={status.color}>{status.label}</Tag>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="弃单信息" style={{ borderRadius: 8, marginBottom: 16 }}>
            <Descriptions column={{ xs: 1, sm: 2 }} size="small">
              <Descriptions.Item label="弃单号">
                <span style={{ fontFamily: 'monospace' }}>#{order.orderNo}</span>
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={status.color}>{status.label}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="客户邮箱">
                {order.email || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="货币">
                {order.currency || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="弃单时间">
                {formatTime(order.abandonedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="恢复时间">
                {formatTime(order.recoveredAt)}
              </Descriptions.Item>
              {order.discountCode && (
                <Descriptions.Item label="优惠码">
                  <Tag>{order.discountCode}</Tag>
                </Descriptions.Item>
              )}
              {order.note && (
                <Descriptions.Item label="客户备注" span={2}>
                  {order.note}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          <Card title="商品明细" style={{ borderRadius: 8 }}>
            <Table
              rowKey={(record) => record.id || `${record.variantId}-${record.sku}`}
              dataSource={items}
              columns={itemColumns}
              pagination={false}
              size="middle"
              locale={{ emptyText: '暂无商品明细' }}
              scroll={{ x: 640 }}
            />
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="收货信息" style={{ borderRadius: 8, marginBottom: 16 }}>
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              <Text strong>
                {`${order.shippingLastName || ''} ${order.shippingFirstName || ''}`.trim() || '-'}
              </Text>
              <Text>{order.shippingPhone || '-'}</Text>
              <Text>{order.shippingAddress1 || '-'}</Text>
              {order.shippingAddress2 && <Text>{order.shippingAddress2}</Text>}
              <Text>
                {[order.shippingCity, order.shippingProvince, order.shippingPostalCode]
                  .filter(Boolean)
                  .join(' ')}
              </Text>
              <Text>{order.shippingCountry || '-'}</Text>
            </Space>
          </Card>

          <Card title="金额汇总" style={{ borderRadius: 8, marginBottom: 16 }}>
            <Space direction="vertical" size={10} style={{ width: '100%' }}>
              <Row justify="space-between">
                <Text type="secondary">商品小计</Text>
                <Text>{formatMoney(order.subtotal)}</Text>
              </Row>
              <Row justify="space-between">
                <Text type="secondary">运费</Text>
                <Text>{formatMoney(order.shippingFee)}</Text>
              </Row>
              <Row justify="space-between">
                <Text type="secondary">折扣</Text>
                <Text type="danger">-{formatMoney(order.discountAmount)}</Text>
              </Row>
              <Divider style={{ margin: '6px 0' }} />
              <Row justify="space-between" align="middle">
                <Text strong>合计</Text>
                <Text strong style={{ fontSize: 20, color: '#7D6B55' }}>
                  {formatMoney(order.total)}
                </Text>
              </Row>
            </Space>
          </Card>

          <Card title="操作" style={{ borderRadius: 8 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              {order.status !== 'recovered' && (
                <Button
                  block
                  type="primary"
                  icon={<ReloadOutlined />}
                  loading={recovering}
                  onClick={handleRecover}
                >
                  标记为已恢复
                </Button>
              )}
              <Button
                block
                danger
                icon={<DeleteOutlined />}
                loading={deleting}
                onClick={handleDelete}
              >
                删除弃单
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
